import { PhoneNumberUtil } from 'google-libphonenumber';
import { CountryISO } from 'ngx-intl-tel-input-gg';

/**
 * Masks an E.164 phone number for display, keeping the country code, the first
 * three and the last two national digits visible and masking the middle in
 * pairs, e.g. "+32494123456" -> "+32 494 ** ** 56".
 *
 * Falls back to the raw value when the input cannot be parsed.
 */
export function maskPhoneNumber(e164: string): string {
  if (!e164) {
    return '';
  }

  try {
    const util = PhoneNumberUtil.getInstance();
    const parsed = util.parse(e164);
    const countryCode = parsed.getCountryCode();
    const national = String(parsed.getNationalNumber());

    if (national.length <= 5) {
      // Too short to reveal 3 + 2 digits and still mask something in between.
      return `+${countryCode} ${national}`;
    }

    const first = national.slice(0, 3);
    const last = national.slice(-2);
    const maskedPairs = '** '.repeat(Math.ceil((national.length - 5) / 2)).trim();

    return `+${countryCode} ${first} ${maskedPairs} ${last}`;
  } catch {
    return e164;
  }
}

const COUNTRY_ISO_VALUES: readonly string[] = Object.values(CountryISO);

export function toCountryISO(alpha2Code: string | undefined | null): CountryISO | undefined {
  const iso = alpha2Code?.toLowerCase();
  return iso && COUNTRY_ISO_VALUES.includes(iso) ? (iso as CountryISO) : undefined;
}

export function browserLocaleCountry(): CountryISO | undefined {
  return toCountryISO(navigator.language?.split('-')[1]);
}

export function resolvePhoneCountry(
  defaultCountryAlpha2Code: string,
  isDefaultCountryForPhoneNumber: boolean,
  preferredCountries: CountryISO[]
): CountryISO | undefined {
  const brandCountry = toCountryISO(defaultCountryAlpha2Code);
  if (isDefaultCountryForPhoneNumber && brandCountry) {
    return brandCountry;
  }
  return browserLocaleCountry() ?? brandCountry ?? preferredCountries[0];
}

/**
 * True when `value` is a complete, dialable number in E.164 form ("+32470123456"),
 * which is what `c-phone` outputs. An empty value is not a valid number.
 */
export function isValidE164(value: unknown): boolean {
  if (typeof value !== 'string' || !value.startsWith('+')) {
    return false;
  }

  try {
    const util = PhoneNumberUtil.getInstance();
    return util.isValidNumber(util.parse(value));
  } catch {
    return false;
  }
}
